/**
 * Definition Converter Utilities
 * Converts between SpreadsheetDefinition format (database) and runtime column config
 */

import { Logger } from 'utilities';
import {
  ColType,
  SortKey,
  SpreadsheetDefinition,
} from '../index';
import {
  COLUMN_TYPES,
  getAllValueTypes,
  getDependentValueTypes,
  getValueType,
  ValueType,
} from '../valueTypes';

// ============================================
// Types for conversion
// ============================================

export interface ColumnConfig {
  id: ColType;
  displayName: string;
  index: number;
  valueType: ValueType;
}

export interface RuntimeSpreadsheetConfig {
  columns: ColumnConfig[];
  sortKeys: SortKey[];
  groupBy: 'company' | 'fund';
  filters: SpreadsheetDefinition['filters'];
  filterExclusions: ColType[];
}

export interface ColumnValidationResult {
  isValid: boolean;
  missingDependencies: string[];
  circularDependencies: string[];
  unknownColumns: string[];
}

// ============================================
// Conversion Functions
// ============================================

/**
 * Convert SpreadsheetDefinition to RuntimeSpreadsheetConfig
 * This transforms the database format into a format usable by the spreadsheet renderer
 */
export function definitionToRuntimeConfig(definition: SpreadsheetDefinition): RuntimeSpreadsheetConfig {

  // Convert columns object to array with value type info
  const columns: ColumnConfig[] = Object.entries(definition.columns)
    .map(([idx, colType]) => ({ index: Number(idx), colType }))
    .sort((a, b) => a.index - b.index)
    .map(({ index, colType }) => {
      const valueType = getValueType(colType);
      if (valueType) {
        return {
          id: colType,
          displayName: valueType.displayName,
          index,
          valueType,
        };
      }
      // Handle unknown column type - create a fallback
      return {
        id: colType,
        displayName: colType,
        index,
        valueType: {
          id: colType,
          displayName: colType,
          displayFormat: { type: 'text' },
          kind: 'simple',
          dependsOn: [],
        }
      }
    });

  return {
    columns,
    sortKeys: definition.sortKeys ?? [],
    groupBy: definition.groupBy ?? 'company',
    filters: definition.filters ?? [],
    filterExclusions: definition.filterExclusions ?? [],
  };
}

/**
 * Convert RuntimeSpreadsheetConfig back to SpreadsheetDefinition
 * This transforms the runtime format back to database format for saving
 */
export function runtimeConfigToDefinition(config: RuntimeSpreadsheetConfig): SpreadsheetDefinition {
  const columns: Record<number, ColType> = config.columns.reduce((acc, col) => {
    acc[col.index] = col.id;
    return acc;
  }, {} as Record<number, ColType>);

  // Alternatively, using a for loop:
  // const columns: Record<number, ColType> = {};
  //
  // for (const col of config.columns) {
  //   columns[col.index] = col.id;
  // }

  return {
    columns,
    sortKeys: config.sortKeys,
    groupBy: config.groupBy,
    filters: config.filters,
    filterExclusions: config.filterExclusions,
  };
}

// ============================================
// Validation Functions
// ============================================

/**
 * Validate a spreadsheet definition
 * Checks for missing dependencies, circular dependencies, and unknown columns
 */
export function validateDefinition(definition: SpreadsheetDefinition): ColumnValidationResult {
  const columnTypes = new Set(Object.values(definition.columns));

  const result: ColumnValidationResult = {
    isValid: true,
    missingDependencies: [],
    circularDependencies: [],
    unknownColumns: [],
  };

  columnTypes.forEach(col => {
    if (!(col in COLUMN_TYPES)) {
      result.unknownColumns.push(col);
    }

    // Check for missing dependencies
    const valueType = getValueType(col);
    if (valueType) {
      valueType.dependsOn.forEach(dep => {
        if (!columnTypes.has(dep)) {
          result.missingDependencies.push(`${col} depends on ${dep} which is not in the definition`);
        }
      });
    } else {
      result.unknownColumns.push(col);
    }
  });

  // Check for circular dependencies (simplified check)
  // A full topological sort would be more thorough
  const visited = new Set<string>();
  const inStack = new Set<string>();

  const detectCycle = (colType: string): boolean => {
    if (inStack.has(colType)) {
      return true;
    }
    if (visited.has(colType)) {
      return false;
    }

    visited.add(colType);
    inStack.add(colType);

    const valueType = getValueType(colType);

    if (valueType) {
      valueType.dependsOn.forEach(dep => {
        if (columnTypes.has(dep) && detectCycle(dep)) {
          result.circularDependencies.push(`${colType} -> ${dep}`);
          throw new Error('Cycle detected');
        }
      });
    }

    inStack.delete(colType);
    return false;
  };

  columnTypes.forEach(colType => {
    try {
      detectCycle(colType);
    } catch (error) { // eslint-disable-line @typescript-eslint/no-unused-vars
      // Cycle detected, already recorded
    }
  });

  result.isValid =
    result.unknownColumns.length === 0 &&
    result.missingDependencies.length === 0 &&
    result.circularDependencies.length === 0;

  return result;
}

/**
 * Get columns that would be affected by removing a column
 * Returns list of dependent column types that would also need to be removed
 */
export function getAffectedColumns(
  definition: SpreadsheetDefinition,
  columnToRemove: ColType
): ColType[] {
  const columnTypes = new Set(Object.values(definition.columns));
  const dependents = getDependentValueTypes(columnToRemove);

  return dependents.filter(dep => columnTypes.has(dep));
}

/**
 * Check if a column can be safely removed
 * Returns true if no other columns depend on it, or if caller acknowledges cascade
 */
export function canRemoveColumn(
  definition: SpreadsheetDefinition,
  columnType: ColType,
  allowCascade = false
): { canRemove: boolean; dependents: ColType[] } {
  const valueType = getValueType(columnType);

  // Check if column is required
  if (valueType?.metadata?.isRequired) {
    return { canRemove: false, dependents: [] };
  }

  const dependents = getAffectedColumns(definition, columnType);

  if (dependents.length === 0) {
    return { canRemove: true, dependents: [] };
  }

  return { canRemove: allowCascade, dependents };
}

/**
 * Remove a column and its dependents from definition
 * Returns the new definition with the column and all dependents removed
 */
export function removeColumnWithDependents(
  definition: SpreadsheetDefinition,
  columnType: ColType
): SpreadsheetDefinition {
  const toRemove = new Set([columnType, ...getAffectedColumns(definition, columnType)]);

  // const newColumns: Record<number, ColType> = {};
  // let newIndex = 0;

  // Rebuild columns excluding removed ones using array iteration
  const entries = Object.entries(definition.columns)
    .map(([idx, col]) => ({ idx: Number(idx), col }))
    .sort((a, b) => a.idx - b.idx);

  const filteredCols = entries.filter(({ col }) => !toRemove.has(col)).map(({ col }) => col);

  // for (const { col } of entries) {
  //   if (!toRemove.has(col)) {
  //     newColumns[newIndex++] = col;
  //   }
  // }

  const newColumns: Record<number, ColType> = filteredCols.reduce((acc, col, idx) => {
    acc[idx] = col;
    return acc;
  }, {} as Record<number, ColType>);

  return {
    ...definition,
    columns: newColumns,
  };
}

// ============================================
// Column Addition Utilities
// ============================================

/**
 * Add a column to a definition with automatic dependency resolution
 * If the column has dependencies not in the definition, adds them first
 */
export function addColumnWithDependencies(
  definition: SpreadsheetDefinition,
  columnType: ColType,
  position?: number
): SpreadsheetDefinition {
  const valueType = getValueType(columnType);
  if (!valueType) {
    Logger.warn(`Unknown column type: ${columnType}`);
    return definition;
  }

  const existingColumns = new Set(Object.values(definition.columns));

  if (existingColumns.has(columnType)) {
    return definition; // Already exists
  }

  // Collect all dependencies that need to be added
  const toAdd: ColType[] = [];

  const collectDeps = (colType: string) => {
    const vt = getValueType(colType);
    if (!vt) {
      return;
    }

    vt.dependsOn.forEach(dep => {
      if (!existingColumns.has(dep) && !toAdd.includes(dep)) {
        collectDeps(dep);
        toAdd.push(dep);
      }
    });
  };

  collectDeps(columnType);
  toAdd.push(columnType);

  // Build new columns
  const entries = Object.entries(definition.columns)
    .map(([idx, col]) => ({ idx: Number(idx), col }))
    .sort((a, b) => a.idx - b.idx);

  const newColumns: Record<number, ColType> = {};
  let newIndex = 0;

  // Insert position (default: end)
  const insertPos = position ?? entries.length;

  const beforeInsert = entries.slice(0, insertPos);
  const afterInsert = entries.slice(insertPos);

  // Add columns before insert position
  beforeInsert.forEach(({ col }) => {
    newColumns[newIndex] = col;
    newIndex += 1;
  });

  // Insert all new columns here
  toAdd.forEach(col => {
    newColumns[newIndex] = col;
    newIndex += 1;
  });

  // Add columns after insert position
  afterInsert.forEach(({ col }) => {
    newColumns[newIndex] = col;
    newIndex += 1;
  });

  return {
    ...definition,
    columns: newColumns,
  };
}

/**
 * Get all available columns that can be added to a definition
 * Excludes columns already in the definition
 */
export function getAvailableColumns(
  definition: SpreadsheetDefinition,
  summaryType: 'firm' | 'fund'
): ValueType[] {
  const existingColumns = new Set(Object.values(definition.columns));
  const allValueTypes = getAllValueTypes();

  return allValueTypes.filter(vt => {
    // Skip if already in definition
    if (existingColumns.has(vt.id)) {
      return false;
    }

    // Filter by summary type
    const vtSummaryType = vt.metadata?.summaryType;
    if (vtSummaryType && vtSummaryType !== 'both' && vtSummaryType !== summaryType) {
      return false;
    }

    return true;
  });
}

// ============================================
// Sort Order Utilities
// ============================================

/**
 * Get the evaluation order for columns based on dependencies
 * Returns columns sorted so that dependencies are calculated before dependents
 */
export function getColumnEvaluationOrder(definition: SpreadsheetDefinition): ColType[] {
  const columns = Object.values(definition.columns);
  const result: ColType[] = [];
  const visited = new Set<string>();
  const temp = new Set<string>();

  const visit = (colType: string) => {
    if (temp.has(colType)) {
      throw new Error(`Circular dependency detected: ${colType}`);
    }
    if (visited.has(colType)) {
      return;
    }

    temp.add(colType);

    const valueType = getValueType(colType);
    if (valueType) {
      valueType.dependsOn.forEach(dep => {
        if (columns.includes(dep)) {
          visit(dep);
        }
      });
    }

    temp.delete(colType);
    visited.add(colType);
    result.push(colType);
  };

  columns.forEach(col => {
    try {
      visit(col);
    } catch (error) {
      Logger.warn(error);
    }
  });

  return result;
}
